import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, Button, Box, TextField, Typography, Chip, Grid, FormControl } from '@mui/material';
import { Close } from '@mui/icons-material';
import { FullScreen, useFullScreenHandle } from '../hooks/useFullScreen';
import { useArrowKeyNav } from '../hooks/useArrowKeyNav';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { isMobile, useMobileOrientation } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { getData, setTimestamp } from '../../api/dataSlice';
import { getDetectionColor, hexToRgb, CloseIconButton, getDashboardRawData, dashboardFmtDate, databaseDateToFilename, filenameToDatabaseDate, fetchFile } from '../Utilities';
import { useNavigateWithQueryParams } from '../hooks/useNavigateWithQueryParams';
import Controlbar from '../controls/Controlbar';
import NavArrows from '../controls/NavArrows';

function getDetectionsDay(dateString, archiveSource, cameraId) {

    const { detailsData } = getDashboardRawData(cameraId)

    // build detection groups by filename
    const byFile = {}
    if (detailsData) {
        const dayRows = detailsData.filter(elem => dashboardFmtDate(new Date(elem.timestamp)) === dateString)
        for (const elem of dayRows) {
            if (!byFile[elem.image_filename]) byFile[elem.image_filename] = []
            byFile[elem.image_filename].push(elem)
        }
    }

    // merge ALL images from archive source (includes those without detections)
    if (archiveSource?.[0]) {
        for (const entry of archiveSource[0]) {
            if (dashboardFmtDate(entry[0]) !== dateString) continue
            const camData = entry[1]?.[cameraId]
            if (!camData?.datum) continue
            const filename = databaseDateToFilename(camData.datum)
            if (!byFile[filename]) byFile[filename] = [] // no detections
        }
    }

    const filenames = Object.keys(byFile).sort()
    if (!filenames.length) return { date: dateString, images: [] }

    return {
        date: dateString,
        images: filenames.map(filename => {
            const group = byFile[filename]
            const detections = group.map(row => {
                const objSubtype = row.object_subtype || ''
                let coords = row.coords || []
                if (Array.isArray(coords)) {
                    coords = coords.map(elem => Array.isArray(elem) && elem.length === 1 && Array.isArray(elem[0]) ? elem[0] : elem)
                }
                return {
                    object_class: (objSubtype && objSubtype.includes('*')) ? objSubtype : (row.object_class || 'unknown'),
                    confidence: Math.round((row.confidence || 0) * 100) / 100,
                    coords,
                    bbox: row.bbox || []
                }
            })
            // reconstruct timestamp from filename for images without detections
            const timestamp = group[0]?.timestamp || (filenameToDatabaseDate(filename) + ':00') || null
            return { filename, timestamp, detections }
        })
    }
}

function formatTimestamp(timestamp) {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    const pad = (n) => String(n).padStart(2, '0')
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const navButtonSx = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    width: 40, height: 40, borderRadius: '50%',
    bgcolor: 'rgba(43, 51, 63, 0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', zIndex: 2,
    '&:hover': { bgcolor: 'rgba(43, 51, 63, 0.9)' }
}

export default function DetectionDialog({ date, onClose, initialEnabledTypes, initialTimestamp, initialClickedType, baseUrl, translateType, cameraId: propCameraId }) {

    const { t } = useTranslation()
    const { isPortrait, isLandscape } = useMobileOrientation()
    const { CAMERAS } = window.conf
    const { source } = useSelector(getData)

    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [currentIdx, setCurrentIdx] = useState(0)
    const [enabledTypes, setEnabledTypes] = useState(new Set())
    const [imgAspect, setImgAspect] = useState(null)
    const [zoom, setZoom] = useState(1)

    const dispatch = useDispatch()
    const navigate = useNavigateWithQueryParams()
    const fullscreenHandle = useFullScreenHandle()
    const zoomResetRef = useRef(null) // stores resetTransform from TransformWrapper
    const canvasRef = useRef(null)
    const imgRef = useRef(null)
    const rafRef = useRef(null)
    const initializedRef = useRef(false)
    const currentIdxRef = useRef(0)

    currentIdxRef.current = currentIdx
    const isMobilePortrait = isMobile && isPortrait
    const isMobileLandscape = isMobile && isLandscape
    const chipCols = isMobilePortrait ? 2 : 4
    const chipHeight = isMobile ? 22 : 24
    const avatarSize = isMobile ? 14 : 18
    const camId = propCameraId ?? CAMERAS?.[0]?.id ?? 0

    // sync `data` with date/source/camera — re-runs when source arrives async (e.g. chatbot drill-down)
    useEffect(() => {
        setLoading(true)
        try {
            const json = getDetectionsDay(date, source, camId)
            setData(json)
        } catch (error) {
            setData(null)
            console.error(`[DetectionDialog]`, error)
        }
        setLoading(false)
    }, [date, source, camId])

    // reset init guard on each new viewer session
    useEffect(() => { initializedRef.current = false }, [date])

    // initialize currentIdx / enabledTypes once per session — guard prevents source refreshes from clobbering user navigation
    useEffect(() => {
        if (initializedRef.current) return
        if (!data?.images?.length) return

        let startIndex = 0
        let timestampMatched = false
        if (initialTimestamp) {
            // nearest-match by ms diff (exact-string match used to fall back to index 0)
            const targetMs = new Date(String(initialTimestamp).replace(' ', 'T')).getTime()
            if (!isNaN(targetMs)) {
                let bestIdx = 0, bestDiff = Infinity
                data.images.forEach((img, i) => {
                    const ts = (img.timestamp || '').replace(' ', 'T')
                    const ms = new Date(ts).getTime()
                    if (!isNaN(ms)) {
                        const diff = Math.abs(ms - targetMs)
                        if (diff < bestDiff) { bestDiff = diff; bestIdx = i }
                    }
                })
                startIndex = bestIdx
                timestampMatched = true
            }
        }
        // type-filter safety net — only when no timestamp matched (so a valid match at idx 0 stands)
        if (!timestampMatched && initialEnabledTypes?.size > 0) {
            let lastMatchIdx = -1
            for (let i = data.images.length - 1; i >= 0; i--) {
                if (data.images[i].detections.some(d => initialEnabledTypes.has(d.object_class))) {
                    lastMatchIdx = i
                    break
                }
            }
            if (lastMatchIdx >= 0) startIndex = lastMatchIdx
        }

        setCurrentIdx(startIndex)

        if (initialClickedType) {
            setEnabledTypes(new Set([initialClickedType]))
        } else {
            const allDayTypes = new Set()
            data.images.forEach(img => img.detections.forEach(detection => allDayTypes.add(detection.object_class)))
            if (initialEnabledTypes?.size > 0) {
                const intersection = new Set([...initialEnabledTypes].filter(elem => allDayTypes.has(elem)))
                setEnabledTypes(intersection.size > 0 ? intersection : allDayTypes)
            } else {
                setEnabledTypes(allDayTypes)
            }
        }

        initializedRef.current = true
    }, [data, initialTimestamp, initialEnabledTypes, initialClickedType, date])

    const drawOverlays = useCallback(() => {

        const canvas = canvasRef.current
        const img = imgRef.current
        if (!canvas || !img || !data?.images?.length) return
        if (!img.naturalWidth || !img.naturalHeight) return

        const image = data.images[currentIdxRef.current]
        if (!image) return

        const ctx = canvas.getContext('2d')

        // compute actual rendered image area (object-fit: contain may letterbox)
        const elemW = img.clientWidth, elemH = img.clientHeight
        const natW = img.naturalWidth, natH = img.naturalHeight
        const imgAspect = natW / natH
        const elemAspect = elemW / elemH
        
        let w, h, ox, oy

        if (imgAspect > elemAspect) {
            w = elemW; h = elemW / imgAspect; ox = 0; oy = (elemH - h) / 2
        } else {
            h = elemH; w = elemH * imgAspect; ox = (elemW - w) / 2; oy = 0
        }

        canvas.width = w
        canvas.height = h
        canvas.style.width = w + 'px'
        canvas.style.height = h + 'px'
        canvas.style.top = (img.offsetTop + oy) + 'px'
        canvas.style.left = (img.offsetLeft + ox) + 'px'
        const scaleX = w / natW, scaleY = h / natH
        ctx.clearRect(0, 0, w, h)

        image.detections.forEach(elem => {
            if (!enabledTypes.has(elem.object_class)) return
            const color = getDetectionColor(elem.object_class)
            const rgb = hexToRgb(color)

            if (elem.coords?.length >= 3) {
                ctx.beginPath()
                ctx.moveTo(elem.coords[0][0] * scaleX, elem.coords[0][1] * scaleY)
                for (let i = 1; i < elem.coords.length; i++) ctx.lineTo(elem.coords[i][0] * scaleX, elem.coords[i][1] * scaleY)
                ctx.closePath()
                ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},0.25)`
                ctx.fill()
                ctx.strokeStyle = color
                ctx.lineWidth = 2
                ctx.stroke()
            } else if (elem.bbox?.length === 2) {
                const x = elem.bbox[0][0] * scaleX, y = elem.bbox[0][1] * scaleY
                const bw = (elem.bbox[1][0] - elem.bbox[0][0]) * scaleX
                const bh = (elem.bbox[1][1] - elem.bbox[0][1]) * scaleY
                ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},0.2)`
                ctx.fillRect(x, y, bw, bh)
                ctx.strokeStyle = color
                ctx.lineWidth = 2
                ctx.strokeRect(x, y, bw, bh)
            }
        })
    }, [data, enabledTypes])

    // coalesce multiple draw triggers (resize + useEffect + onLoad) into one per frame
    const scheduleRedraw = useCallback(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current)
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null
            drawOverlays()
        })
    }, [drawOverlays])

    useEffect(() => {
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
    }, [])

    const handleImgLoad = useCallback(() => {
        const img = imgRef.current
        if (img && img.naturalWidth && img.naturalHeight) {
            setImgAspect(img.naturalWidth / img.naturalHeight)
        }
        scheduleRedraw()
    }, [scheduleRedraw])

    // redraw on data/types/aspect changes — batched via rAF (fullscreen handled by resize listener)
    useEffect(() => scheduleRedraw(), [scheduleRedraw, imgAspect])

    useEffect(() => {
        window.addEventListener('resize', scheduleRedraw)
        return () => window.removeEventListener('resize', scheduleRedraw)
    }, [scheduleRedraw])

    useArrowKeyNav({
        enabled: true,
        canPrev: currentIdx > 0,
        canNext: !!data?.images && currentIdx < data.images.length - 1,
        onPrev: () => { zoomResetRef.current?.(); setCurrentIdx(prev => prev - 1) },
        onNext: () => { zoomResetRef.current?.(); setCurrentIdx(prev => prev + 1) },
        onEscape: onClose
    })

    const toggleType = useCallback((type) => {
        setEnabledTypes(prev => { const next = new Set(prev); next.has(type) ? next.delete(type) : next.add(type); return next })
    }, [])

    // only types that exist on this day, sorted alphabetically
    const dayTypes = useMemo(() => {
        if (!data?.images) return []
        const set = new Set()
        data.images.forEach(img => img.detections.forEach(detection => set.add(detection.object_class)))
        return [...set].sort((a, b) => translateType(a).localeCompare(translateType(b)))
    }, [data, translateType])

    const chipRows = Math.ceil(dayTypes.length / chipCols)

    if (loading || !data?.images?.length) {
        return (
            <Dialog open onClose={onClose} disableEnforceFocus>
                <CloseIconButton onClick={onClose} />
                <DialogContent sx={{ p: 2, minWidth: 250 }}>
                    <Typography sx={{ textAlign: 'center', py: 4 }}>
                        {loading ? `${t('pages.dashboard.loading_images')} ${date}...` : `${t('pages.dashboard.no_images')} ${date}.`}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="outlined" onClick={onClose}>{t('actions.close')}</Button>
                    </Box>
                </DialogContent>
            </Dialog>
        )
    }

    const currentImage = data.images[currentIdx]
    const currentCounts = {}
    
    currentImage.detections.forEach(elem => { currentCounts[elem.object_class] = (currentCounts[elem.object_class] || 0) + 1 })
    
    const allDayActive = dayTypes.length > 0 && dayTypes.every(elem => enabledTypes.has(elem))
    const hasPrev = currentIdx > 0
    const hasNext = currentIdx < data.images.length - 1

    // open the image archive on the same moment as the viewed detection image (setTimestamp snaps to the nearest archive index)
    const handleToArchive = () => {
        if (currentImage?.timestamp) dispatch(setTimestamp(String(currentImage.timestamp).replace(' ', 'T')))
        navigate(window.conf.ROUTES?.ARCHIVE?.path || '/archive')
    }

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="md"
            fullWidth
            disableEnforceFocus
            PaperProps={isMobile ? {
                sx: {
                    m: 1,
                    width: 'calc(100% - 16px)',
                    maxHeight: 'calc(100% - 16px)',
                    ...(isLandscape && {
                        bgcolor: '#0a0a1a',
                        ...(imgAspect && {
                            width: `min(calc(100vw - 16px), calc((100vh - 16px) * ${imgAspect}))`,
                            height: `min(calc(100vh - 16px), calc((100vw - 16px) / ${imgAspect}))`,
                            maxWidth: 'none',
                            maxHeight: 'none'
                        })
                    })
                }
            } : undefined}
        >

            {/* close button */}

            {isMobileLandscape ? (
                <Box onClick={onClose} sx={{ ...navButtonSx, top: 8, right: 8, transform: 'none' }}>
                    <Close sx={{ color: 'white', fontSize: 22 }} />
                </Box>
            ) : (
                <CloseIconButton onClick={onClose} />
            )}

            <DialogContent sx={{ p: isMobileLandscape ? 0 : 2 }}>
                <Grid container>
                    {!isMobileLandscape && <Grid item xs={12} sx={{ pb: 1 }} />}

                    {CAMERAS.length > 1 && !isMobileLandscape && (
                        <Grid item xs={12} sx={{ pb: isMobile ? 1 : 2 }}>
                            <FormControl fullWidth variant="outlined">
                                <TextField label={t('events.camera')} value={CAMERAS[0]?.name || `${t('events.camera')} 1`} disabled />
                            </FormControl>
                        </Grid>
                    )}

                    {!isMobileLandscape && (
                        <Grid item xs={12} sx={{ pb: isMobile ? 1 : 2 }}>
                            <FormControl fullWidth variant="outlined">
                                <TextField label={t('date.date_n_time')} value={formatTimestamp(currentImage.timestamp)} disabled />
                            </FormControl>
                        </Grid>
                    )}

                    {!isMobileLandscape && (
                        <Grid item xs={12} sx={{ pb: isMobile ? 1 : 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, flexGrow: 1, ml: 0.5 }}>
                                    {t('pages.dashboard.object_types')}
                                </Typography>
                                {!isMobilePortrait && (
                                    <Typography variant="caption" color="text.secondary">
                                        {t('pages.dashboard.image')} {currentIdx + 1} / {data.images.length} - {currentImage.detections.length} {t('pages.dashboard.total_detections')}
                                    </Typography>
                                )}
                                <Button
                                    variant="contained"
                                    size="small"
                                    disabled={allDayActive || currentImage.detections.length === 0}
                                    onClick={() => setEnabledTypes(new Set(dayTypes))}
                                    sx={{ minWidth: 'auto', px: 1.5, py: 0.25, fontSize: 12 }}
                                >
                                    {t('pages.dashboard.all')}
                                </Button>
                                <Button variant="outlined" size="small" disabled={enabledTypes.size === 0 || currentImage.detections.length === 0} onClick={() => setEnabledTypes(new Set())} sx={{ minWidth: 'auto', px: 1.5, py: 0.25, fontSize: 12 }}>
                                    {t('pages.dashboard.none')}
                                </Button>
                            </Box>
                        </Grid>
                    )}

                    {!isMobileLandscape && (
                        <Grid item xs={12} sx={{ pb: isMobile ? 1 : 2 }}>
                            <Box sx={{
                                display: 'grid',
                                gridAutoFlow: 'column',
                                gridTemplateRows: `repeat(${chipRows}, auto)`,
                                gridTemplateColumns: `repeat(${chipCols}, 1fr)`,
                                gap: 0.75
                            }}>
                                {dayTypes.map(elem => {
                                    const count = currentCounts[elem] || 0
                                    const active = enabledTypes.has(elem) && count > 0
                                    return (
                                        <Chip
                                            key={elem}
                                            size="small"
                                            avatar={<Box sx={{ borderRadius: '50%', bgcolor: getDetectionColor(elem) }} />}
                                            label={`${translateType(elem)} (${count})`}
                                            variant={active ? 'filled' : 'outlined'}
                                            onClick={() => count > 0 && toggleType(elem)}
                                            onDoubleClick={() => count > 0 && setEnabledTypes(new Set([elem]))}
                                            sx={{
                                                height: chipHeight,
                                                fontSize: isMobile ? 12 : undefined,
                                                cursor: count > 0 ? 'pointer' : 'default',
                                                justifyContent: 'flex-start',
                                                overflow: 'hidden',
                                                opacity: count > 0 ? 1 : 0.4,
                                                '& .MuiChip-avatar': { width: avatarSize, height: avatarSize, minWidth: avatarSize, ml: '4px' },
                                                '& .MuiChip-label': { pl: '8px' },
                                                ...(active && { bgcolor: '#e6ecf3', borderColor: '#003366' })
                                            }}
                                        />
                                    )
                                })}
                            </Box>
                        </Grid>
                    )}

                    <Grid item xs={12} sx={{ pb: isMobileLandscape ? 0 : 2 }}>
                        <TransformWrapper
                            disablePadding
                            doubleClick={{ disabled: true }}
                            onZoomStop={ref => { ref.instance.setup.panning.disabled = ref.state.scale <= 1 }}
                            onPanning={ref => { ref.instance.setup.panning.disabled = ref.state.scale <= 1 }}
                            onInit={ref => { ref.instance.setup.panning.disabled = true }}
                            onTransformed={ref => setZoom(ref.state.scale)}
                        >
                            {(utils) => {
                                zoomResetRef.current = utils.resetTransform
                                return (
                                    <FullScreen handle={fullscreenHandle} onChange={() => { utils.resetTransform(); scheduleRedraw() }}>
                                        <Box
                                            className={isMobile ? "" : "fullscreen"}
                                            onDoubleClick={() => fullscreenHandle.active ? fullscreenHandle.exit() : fullscreenHandle.enter()}
                                            sx={{
                                                position: 'relative', textAlign: 'center', bgcolor: '#0a0a1a', borderRadius: 1,
                                                overflow: 'hidden', userSelect: 'none', touchAction: 'manipulation',
                                                ...(fullscreenHandle.active && { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' })
                                            }}
                                        >
                                            <TransformComponent
                                                wrapperStyle={{ width: '100%', height: '100%' }}
                                                contentStyle={{ width: '100%', height: '100%' }}
                                            >
                                                <img
                                                    ref={imgRef}
                                                    src={`${baseUrl}${currentImage.filename}`}
                                                    alt={currentImage.filename}
                                                    onLoad={handleImgLoad}
                                                    draggable={false}
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: fullscreenHandle.active ? '100vh' : (isMobileLandscape ? '100%' : '45vh'),
                                                        width: isMobileLandscape ? '100%' : undefined,
                                                        height: isMobileLandscape ? '100%' : undefined,
                                                        objectFit: 'contain',
                                                        display: 'block',
                                                        margin: isMobileLandscape ? 0 : '0 auto'
                                                    }} />
                                                <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }} />
                                            </TransformComponent>
                                            <NavArrows
                                                hasPrev={hasPrev}
                                                hasNext={hasNext}
                                                onPrev={() => { utils.resetTransform(); setCurrentIdx(prev => prev - 1) }}
                                                onNext={() => { utils.resetTransform(); setCurrentIdx(prev => prev + 1) }}
                                            />
                                            <Controlbar
                                                fullscreenHandle={fullscreenHandle}
                                                zoom={zoom}
                                                zoomUtils={utils}
                                                handleTakeSnapshot={() => fetchFile(`${baseUrl}${currentImage.filename}`, `${t("pages.dashboard.tab_detections")}_${currentImage.filename}`)}
                                                imageSource={`${baseUrl}${currentImage.filename}`}
                                                hasLastModified={false}
                                            />
                                        </Box>
                                    </FullScreen>
                                )
                            }}
                        </TransformWrapper>
                    </Grid>

                    {!isMobileLandscape && (
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button variant="contained" onClick={handleToArchive}>{t('pages.dashboard.to_image_archive')}</Button>
                            <Button variant="outlined" onClick={onClose} sx={{ width: 100 }}>{t('actions.close')}</Button>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
        </Dialog>
    )
}
